import React, { useState, useEffect } from 'react';
import { RotateCcw, Trash2, AlertTriangle, CheckCircle2, ClipboardList, Loader2, Plus, Droplets, Waves, MoreHorizontal, Sparkles, Lock } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Field, inputCls, labelCls } from './MlsFields';
import { MlsEntityContact } from './MlsEntityContact';
import { entryHasData, entryIssue, ruleActive, ENTITY_REQUIRED_MSG } from './mlsServiceModel';
import { UTILITY_TYPES, UTILITY_TYPE_OPTIONS, UTILITY_TYPE_LABEL, UTILITY_LEGACY_TYPE_OPTION, UTILITY_SOURCE_OPTIONS, UTILITY_SOURCE_LABEL, entryOffGrid, utilityFindingsGate, UTILITY_FINDINGS_GATE_MSG } from './mlsServiceTypes';
import { OffGridBadge } from './MlsUtilitySource';
import { generateFindings, fieldApplicable, applyFindingChange, DEPENDENT_FIELDS } from './mlsAutoFindings';

const STATUS = {
  complete: { label: 'Complete', badge: 'bg-emerald-400/20 text-white border-emerald-200/60', Icon: CheckCircle2 },
  in_progress: { label: 'In Progress', badge: 'bg-rose-100 text-rose-700 border-rose-200', Icon: AlertTriangle },
  empty: { label: 'Not started', badge: 'bg-white/15 text-white border-white/30', Icon: null },
};
const UTILITY_ICON = { water: Droplets, sewer: Waves, trash: Trash2, other: MoreHorizontal };

const SearchFindings = ({ item, index, disabled: locked, set }) => {
  const { def, entry, noneFound, section } = item;
  const prefix = `mls-${def.key}-entry-${index}`;
  const hasFindings = def.findingFields.length > 0;
  const active = ruleActive(def, entry);
  const issue = entryIssue(def, entry);
  const findings = generateFindings(section, entry, noneFound);
  const hasNotes = !!(entry.notes || '').trim();
  const [notesOpen, setNotesOpen] = useState(hasNotes);
  // once notes have content (e.g. the well/septic auto note) the textarea stays mounted — no collapse/jump on toggles
  useEffect(() => { if (hasNotes) setNotesOpen(true); }, [hasNotes]);
  const visible = def.findingFields.filter((f) => fieldApplicable(section, entry, f.k));
  const offGrid = entryOffGrid(entry); // MLS-UTILITY-WELL-SEPTIC-001: no public account fields to fill
  // MLS-UTILITY-FINDINGS-GATE-001: Utility findings stay disabled (values kept) until source + entity prerequisites are met
  const gate = item.utilityType ? utilityFindingsGate(entry, item.utilityType, def.entityField) : null;
  const gated = !locked && !!gate;
  const disabled = locked || gated;
  return (
    <section className="rounded-xl border border-indigo-100 bg-white overflow-hidden shadow-sm" data-testid={`${prefix}-search-findings`} data-gated={gate || undefined}>
      <header className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-50/80 to-violet-50/60 border-b border-indigo-100">
        <h5 className="flex items-center gap-2 text-[11px] font-bold text-slate-800 uppercase tracking-wide">
          <span className="mls-tile bg-indigo-100 text-indigo-600" style={{ width: 24, height: 24, borderRadius: 7 }}><ClipboardList className="w-3.5 h-3.5" /></span>{hasFindings ? 'Search Findings' : 'Notes'}
        </h5>
        {gated && (
          <span className="mls-pop inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 normal-case tracking-normal" data-testid={`${prefix}-findings-gated`}>
            <Lock className="w-3 h-3" />Awaiting {gate === 'source' ? 'source' : 'entity'} selection
          </span>
        )}
        {hasFindings && noneFound && (
          <span className="text-[10px] font-semibold text-slate-500 normal-case tracking-normal" data-testid={`${prefix}-findings-none-found`}>Finding fields cleared</span>
        )}
        {hasFindings && !noneFound && offGrid && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 normal-case tracking-normal" data-testid={`${prefix}-off-grid-note`}>
            <CheckCircle2 className="w-3 h-3" />{UTILITY_SOURCE_LABEL[entry.utility_source]} confirmed — no public account, balance or lien details required
          </span>
        )}
      </header>
      <div className={`p-3 space-y-2.5 mls-findings-body ${gated ? 'mls-findings-gated' : ''}`}>
        {gated && (
          <p className="mls-reveal flex items-center gap-1.5 text-[11px] font-medium text-slate-600 bg-slate-50 border border-dashed border-slate-300 rounded-md px-2 py-1" data-testid={`${prefix}-findings-gate-msg`}>
            <Lock className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />{UTILITY_FINDINGS_GATE_MSG}
          </p>
        )}
        {hasFindings && !noneFound && (
          <div className={`grid grid-cols-2 gap-2 ${def.findingFields.length >= 6 ? 'md:grid-cols-6' : 'md:grid-cols-5'}`} data-testid={`${prefix}-finding-fields`} data-off-grid={offGrid || undefined}>
            {/* off-grid: the public account fields stay in place (disabled, N/A) so the card never jumps */}
            {(offGrid ? def.findingFields : visible).map((f) => {
              const req = active && def.findingRule?.required.includes(f.k);
              const dependent = !!DEPENDENT_FIELDS[section]?.fields.includes(f.k);
              const val = entry[f.k];
              const legacy = f.type === 'select' && val && !f.options.includes(val) ? [[val, f.legacyOptions?.[val] || `${val} (legacy)`]] : [];
              return (
                <Field key={f.k} label={req ? <span data-required="true">{f.label} <span className="text-rose-500">*</span></span> : f.label}
                  type={offGrid ? 'text' : (f.type || 'text')} value={offGrid ? '' : val} disabled={disabled} readOnly={offGrid} placeholder={offGrid ? 'N/A' : undefined}
                  className={dependent && !offGrid ? 'mls-reveal' : ''}
                  options={f.type === 'select' && !offGrid ? [...legacy, ...f.options] : undefined}
                  onChange={(v) => set(f.k, v)} testId={`${prefix}-${f.k}`} />
              );
            })}
          </div>
        )}
        {hasFindings && !noneFound && issue && (
          <p className="mls-reveal flex items-center gap-1.5 text-[11px] font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1" role="alert" data-testid={`${prefix}-finding-error`}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />{issue}
          </p>
        )}
        <div className="mls-findings-card rounded-lg border border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-white px-2.5 py-1.5" data-testid={`${prefix}-auto-findings`} data-empty={!findings}>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold text-indigo-600 uppercase tracking-wider"><Sparkles className="w-3 h-3" />Auto-Generated Findings</div>
          <p className={`mt-0.5 text-[12px] leading-snug ${findings ? 'text-slate-800' : 'text-slate-400'}`} data-testid={`${prefix}-auto-findings-text`}>
            {findings || 'Findings appear here as results are confirmed.'}
          </p>
        </div>
        {notesOpen || hasNotes ? (
          <div className="mls-reveal">
            <label className={labelCls}>Additional Notes <span className="normal-case tracking-normal font-normal text-slate-400">(optional)</span></label>
            <textarea rows={2} value={entry.notes || ''} disabled={disabled} placeholder="Processor observations…" autoFocus={notesOpen && !hasNotes && !disabled}
              onChange={(e) => set('notes', e.target.value)} className={`${inputCls} h-auto py-1.5 min-h-[40px] resize-y`}
              data-testid={`${prefix}-notes`} />
          </div>
        ) : !disabled && (
          <button type="button" onClick={() => setNotesOpen(true)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors" data-testid={`${prefix}-notes-toggle`}>
            <Plus className="w-3 h-3" />Additional Notes
          </button>
        )}
      </div>
    </section>
  );
};

/* MLS-SVC-NAV-001 / MLS-UTILITY-001: ONE dynamic workspace for the selected service.
   Entry tabs ("Code 1 | Code 2 | +", "Water 1 | Sewer 1 | Trash 1 | + Add") pick the
   authority searched. Everything reads/writes through the service model (storage unchanged). */
export const MlsServiceWorkspace = ({ model, disabled, resolution, jurCtx, saveState, activeEntry, onActiveEntry }) => {
  const { def, items } = model;
  const idx = Math.min(activeEntry || 0, items.length - 1);
  const item = items[idx];
  const [pendingRemove, setPendingRemove] = useState(null);
  const [pendingReset, setPendingReset] = useState(null);
  const prefix = `mls-section-${def.key}`;
  const eprefix = `mls-${def.key}-entry-${idx}`;
  const st = STATUS[model.status];
  const Icon = def.icon;
  const composite = !!model.setType;
  const requestRemove = (i) => (entryHasData(items[i].entry) ? setPendingRemove(i) : doRemove(i));
  const doRemove = (i) => { model.remove(i); onActiveEntry(i <= idx && idx > 0 ? idx - 1 : idx); };
  const removeDisabled = items.length <= 1 && !entryHasData(items[0].entry);
  // MLS-SEARCH-FINDINGS-ENHANCEMENT-001: a flag leaving its positive state nulls its dependent fields
  const set = (k, v) => model.setEntry(idx, applyFindingChange(item.section, item.entry, k, v));
  const addBtnCls = 'h-6 px-1.5 inline-flex items-center gap-1 rounded-md border border-dashed border-blue-300 text-blue-600 text-[11px] font-semibold hover:bg-blue-50 transition-colors';

  const typeField = composite ? {
    value: item.utilityType,
    options: UTILITY_LEGACY_TYPE_OPTION[item.utilityType] ? [UTILITY_LEGACY_TYPE_OPTION[item.utilityType], ...UTILITY_TYPE_OPTIONS] : UTILITY_TYPE_OPTIONS,
    onChange: (t) => { const ni = model.setType(idx, t); if (ni !== idx) onActiveEntry(ni); },
  } : null;
  // MLS-UTILITY-WELL-SEPTIC-001: Water Source / Sewer Type selector (Water and Sewer entries only)
  const sourceField = composite && UTILITY_SOURCE_OPTIONS[item.utilityType]
    ? { utype: item.utilityType, onChange: (v) => model.setSource(idx, v), error: model.issues?.[idx] && model.issues[idx] !== ENTITY_REQUIRED_MSG ? model.issues[idx] : null } : null;

  return (
    <div className="mls-ws-in flex-1 min-w-0 rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_6px_18px_rgba(15,23,42,0.05)] overflow-hidden" data-testid={prefix} data-status={model.status}>
      <div className="mls-card-head flex items-center justify-between gap-2 px-3.5 py-2.5">
        <h4 className="flex items-center gap-2 text-[15px] font-bold text-white tracking-tight truncate" data-testid={`${prefix}-title`}>
          <span className="h-7 w-7 grid place-items-center rounded-lg bg-white/15 border border-white/20"><Icon className="w-4 h-4" /></span>{def.title}
          {def.subtitle && <span className="text-[11px] font-medium text-white/70 hidden md:inline">{def.subtitle}</span>}
        </h4>
        <div className="flex items-center gap-2 flex-shrink-0">
          {saveState === 'saving' && <span className="text-[10px] text-white/80 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Saving</span>}
          {saveState === 'saved' && <span className="mls-pop text-[10px] text-white inline-flex items-center gap-1" data-testid={`${eprefix}-saved`}><CheckCircle2 className="w-3 h-3" />Saved</span>}
          <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold rounded-full border px-2 py-0.5 ${st.badge}`} data-testid={`${prefix}-status`}>
            {st.Icon && <st.Icon className="w-3 h-3" />}{st.label}
          </span>
          {!disabled && (
            <>
              <button type="button" onClick={() => setPendingReset(idx)} disabled={!entryHasData(item.entry)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-white/85 hover:text-white disabled:opacity-40 transition-colors" title={`Clear ${item.label}`}
                data-testid={`${eprefix}-reset`}><RotateCcw className="w-3 h-3" />Reset</button>
              <button type="button" onClick={() => requestRemove(idx)} disabled={removeDisabled}
                className="h-6 w-6 grid place-items-center rounded text-white/70 hover:text-white hover:bg-white/15 disabled:opacity-30 transition-colors" title={`Remove ${item.label}`}
                data-testid={`${eprefix}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
      </div>

      {/* Level 2 navigation — which authority/entity entry of this service */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-1 overflow-x-auto" role="tablist" data-testid={`${prefix}-tabs`}>
          {items.map((it, i) => {
            const TIcon = composite ? UTILITY_ICON[it.utilityType] || Droplets : null;
            const tabIssue = !!model.issues?.[i];
            return (
              <button key={i} type="button" role="tab" aria-selected={i === idx} onClick={() => onActiveEntry(i)}
                title={tabIssue ? `${it.label}: ${model.issues[i]}` : undefined}
                className={`mls-entry-tab inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold whitespace-nowrap border ${i === idx
                  ? 'bg-white text-indigo-700 border-indigo-300 shadow-sm'
                  : 'text-slate-500 border-transparent hover:text-indigo-600 hover:bg-white/70'} ${tabIssue && i !== idx ? 'text-amber-700' : ''}`}
                data-testid={`${prefix}-tab-${i}`} data-has-data={entryHasData(it.entry)} data-utility-type={it.utilityType || undefined} data-issue={tabIssue || undefined}>
                {TIcon && <TIcon className="w-3 h-3" />}{it.label}
                <OffGridBadge value={it.entry?.utility_source} compact testId={`${prefix}-tab-${i}-off-grid`} />
                {tabIssue && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-amber-500 ring-2 ring-amber-200" data-testid={`${prefix}-tab-${i}-issue`} />}
              </button>
            );
          })}
          {!disabled && !composite && (
            <button type="button" onClick={() => onActiveEntry(model.add(idx))} className={`${addBtnCls} w-6 px-0 justify-center`}
              title={def.addLabel} data-testid={`${prefix}-add-entry-btn`}><Plus className="w-3.5 h-3.5" /></button>
          )}
          {!disabled && composite && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={addBtnCls} title={def.addLabel} data-testid={`${prefix}-add-entry-btn`}><Plus className="w-3.5 h-3.5" />Add</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44" data-testid={`${prefix}-add-menu`}>
                <DropdownMenuLabel className="text-xs">Add Utility Entry</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {UTILITY_TYPES.map(([t, label]) => {
                  const TIcon = UTILITY_ICON[t];
                  return (
                    <DropdownMenuItem key={t} className="text-xs gap-2" onSelect={() => onActiveEntry(model.add(t))} data-testid={`${prefix}-add-${t}`}>
                      <TIcon className="w-3.5 h-3.5 text-blue-600" />{label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <span className="ml-1 text-[11px] text-slate-400 font-medium" data-testid={`${prefix}-count`}>({model.count} {model.count === 1 ? 'entry' : 'entries'})</span>
          {model.satisfied
            ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" data-testid={`${prefix}-ok`} />
            : <span className="text-[10px] text-amber-600 font-medium" data-testid={`${prefix}-required-hint`}>required</span>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-testid={`${prefix}-entry-controls`}>
          {model.noneFound && (
            <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-wide" data-testid={`${prefix}-none-found-badge`}>None Found</span>
          )}
          {model.partialNone && (
            <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold" title="Legacy per-section flag" data-testid={`${prefix}-none-found-partial`}>None Found: {model.partialNone}</span>
          )}
          {!disabled && (
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={model.noneFound}
                onChange={(e) => model.setNoneFound(e.target.checked)}
                className="w-3.5 h-3.5 accent-blue-600" data-testid={`${prefix}-none-found-checkbox`} />
              None Found
            </label>
          )}
        </div>
      </div>

      <div key={`${idx}-${item.src || ''}`} className="mls-card-in p-3 space-y-3 bg-slate-50/40" data-testid={eprefix} data-utility-type={item.utilityType || undefined}>
        <MlsEntityContact def={item.def} entry={item.entry} index={idx} service={resolution?.services?.[item.serviceType]} serviceType={item.serviceType}
          disabled={disabled} typeField={typeField} sourceField={sourceField} typeLabel={composite ? UTILITY_TYPE_LABEL[item.utilityType] : null}
          onChange={(e) => model.setEntry(idx, e)} orderId={jurCtx?.orderId} resolution={resolution} onRequested={jurCtx?.reload} onSetJurisdiction={jurCtx?.setJurisdiction} parcelIndex={jurCtx?.parcelIndex || 1} storageIndex={item.srcIdx ?? idx}
          entityError={!disabled && model.issues?.[idx] === ENTITY_REQUIRED_MSG ? 'Select the jurisdiction/entity for this search — required even when the result is None Found.' : null} />
        <SearchFindings item={item} index={idx} disabled={disabled} set={set} />
      </div>

      <AlertDialog open={pendingRemove !== null} onOpenChange={(o) => { if (!o) setPendingRemove(null); }}>
        <AlertDialogContent className="sm:max-w-sm" data-testid={`${prefix}-remove-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Remove {items[pendingRemove ?? 0]?.label}?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">This entry contains data. It will be removed from {def.title} and cannot be restored.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${prefix}-remove-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { doRemove(pendingRemove); setPendingRemove(null); }}
              data-testid={`${prefix}-remove-confirm-btn`}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingReset !== null} onOpenChange={(o) => { if (!o) setPendingReset(null); }}>
        <AlertDialogContent className="sm:max-w-sm" data-testid={`${prefix}-reset-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Reset {items[pendingReset ?? 0]?.label}?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">Clears this entry's property-specific work — findings, notes and selections — for this order. Jurisdiction Directory master data is never modified; if a routing rule applies, the directory entity is re-populated automatically (that is reference data, not a finding).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${prefix}-reset-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-blue-600 hover:bg-blue-700" onClick={() => { model.reset(pendingReset); setPendingReset(null); }}
              data-testid={`${prefix}-reset-confirm-btn`}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
